import { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCatalystFlow } from '@/contexts/CatalystFlowContext';
import { MessageBubble } from './MessageBubble';
import { QuickActionChips } from './QuickActionChips';
import { ActionConfirmation } from './ActionConfirmation';
import { StreamingIndicator } from './StreamingIndicator';
import { Sparkles, Send, Trash2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export function AskCatalystFlowPanel() {
  const {
    isOpen,
    setIsOpen,
    messages,
    sendMessage,
    confirmAction,
    rejectAction,
    isLoading,
    clearMessages,
    pendingPrompt,
    clearPendingPrompt,
  } = useCatalystFlow();


  const [input, setInput] = useState('');
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Handle pending prompt from contextual triggers
  useEffect(() => {
    if (isOpen && pendingPrompt) {
      setInput(pendingPrompt);
      clearPendingPrompt();
      // Focus input
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, pendingPrompt, clearPendingPrompt]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Track loading state changes
  useEffect(() => {
    if (isLoading && !loadingStartTime) {
      setLoadingStartTime(Date.now());
    } else if (!isLoading && loadingStartTime) {
      setLoadingStartTime(null);
    }
  }, [isLoading, loadingStartTime]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!input.trim() || isLoading) return;
    
    const message = input;
    setInput('');
    setLoadingStartTime(Date.now());
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleQuickAction = (prompt: string) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-md md:max-w-lg p-0 flex flex-col"
      >
        <SheetHeader className="p-4 pb-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-base">Ask Catalyst Flow</SheetTitle>
                <p className="text-xs text-muted-foreground">Your AI Co-Founder</p>
              </div>
            </div>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearMessages}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="space-y-4">
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-medium text-foreground mb-1">How can I help?</h3>
                <p className="text-sm text-muted-foreground max-w-[280px] mx-auto">
                  I can analyze your business, suggest actions, and help you move money forward.
                </p>
              </div>
              <QuickActionChips onSelect={handleQuickAction} />
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div key={message.id} className="space-y-2">
                  <MessageBubble message={message} />
                  
                  {/* Pending Actions */}
                  {message.pendingActions && message.pendingActions.length > 0 && (
                    <div className="ml-10 space-y-2">
                      {message.pendingActions.map((action) => (
                        <ActionConfirmation
                          key={action.id}
                          action={action}
                          onConfirm={() => confirmAction(action.id)}
                          onReject={() => rejectAction(action.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && !messages[messages.length - 1]?.isStreaming && (
                <StreamingIndicator startTime={loadingStartTime || undefined} />
              )}
            </div>
          )}
        </ScrollArea>

        <form onSubmit={handleSubmit} className="p-4 border-t bg-background">
          <div className="flex gap-2">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about your business..."
              className={cn(
                "min-h-[44px] max-h-[120px] resize-none",
                "focus-visible:ring-primary"
              )}
              rows={1}
              disabled={isLoading}
            />
            <Button 
              type="submit" 
              size="icon" 
              disabled={!input.trim() || isLoading}
              className="h-[44px] w-[44px] shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 text-center">
            Catalyst Flow uses AI to analyze your business data
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
